// ============================================================================
// shared user-directory registry
// ============================================================================
// The signed-in caller's own profile is authentication readiness. The admin
// roster is a different, opt-in data product: it lists every profile and is
// used only by user management and the Webforms availability editor. Keeping
// the two names and receipts separate prevents a future boot path from
// mistaking the roster for the one profile that resolves authorization.
// ============================================================================

import {resourceKey} from '../app/data/routeDataCatalog.js';
import type {AuthenticatedScope, QueryKey} from '../app/data/routeDataCatalog.js';

export const USER_DIRECTORY_RESOURCE = Object.freeze({
  id: 'shared.user-directory.profiles',
  owner: 'shared.user-directory',
});

// AuthContext's boot coordinator owns this generation. It is deliberately
// separate from `authState`: an unchanged subject may start a new session.
export type UserDirectorySessionEpoch = number;

// This is deliberately a receipt, not a surface declaration. The directory is
// a shared admin interaction that can be reached from many authenticated
// surfaces, so putting it in the surface manifest would falsely make every
// surface a directory owner.
export const USER_DIRECTORY_RECEIPT = Object.freeze({
  resourceId: USER_DIRECTORY_RESOURCE.id,
  owner: USER_DIRECTORY_RESOURCE.owner,
  table: 'profiles',
  trigger: 'admin opens Users or WebformsAdminView needs its Team Availability roster',
  scope: 'authenticated subject/effective role/preview role plus AuthContext sessionEpoch',
  profileReadinessOwner: 'App.loadUser resolves only the signed-in caller profile for authentication readiness',
  failure:
    'retain only same-scope rows and log the failed directory request; a later open or roster retry may request again',
});

/**
 * Directory rows are never keyed by a bare resource name. The subject scope
 * prevents shared-device cache bleed, while the AuthContext session epoch
 * forces a fresh key even when the same person signs back in with the same role.
 */
export function userDirectoryQueryKey(scope: AuthenticatedScope, sessionEpoch: UserDirectorySessionEpoch): QueryKey {
  if (scope.kind !== 'authenticated') {
    throw new Error('userDirectoryQueryKey: an authenticated scope is required');
  }
  return resourceKey(USER_DIRECTORY_RESOURCE.id, scope, {sessionEpoch});
}
